import { GrundstueckId } from '../../../../core/gb-api';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ComponentLocationStrategy } from '../../../services/location-strategy/component-location-strategy';
import { GrundstueckBeschreibungLocationStrategy } from '../../../services/location-strategy/grundstueck-detail-beschreibung-location-strategy.';
import { AvflaecheService, GrundstueckFlaecheMasterDetailEntryMeta } from '../../../../core/av-api';
import { AppConfigService } from '../../../../core/services/app-config.service';
import { TimepointService } from '../../../../core/services/timepoint.service';
import { HistoryMode } from '../../../../core/services/HistoryMode';
import { AppPermissionService } from '../../../../core/services/app-permission.service';
import { AbstractComponent } from '../../../components/abstract-component/abstract.component';
import { ListLoaderObservable } from '../../../components/list-loader/list-loader-observable';
import { ListLoader } from '../../../components/list-loader/list-loader';
import { tap } from 'rxjs/operators';
import { FieldMetaData } from '../../../../core/av-api/model/fieldMetaData';
import { FormatConfigService } from 'app/core/services/format-config.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { ErweiterungConfigResponse } from 'app/core/base-api';
import { BeschreibungTableWrapperComponent } from '../../../components/table/beschreibung-table-wrapper.component';
import { ListLoaderWrapperComponent } from '../../../components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { HistoryDirective } from '../../../directives/history.directive';
import { KompaktTimepointComponent } from '../../../kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { RechtsgrundComponent } from '../../../kompaktdarstellung/rechtsgrund/rechtsgrund.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DecimalHochkommaPipe } from '../../../pipes/simple/decimalHochkomma.pipe';
import { Subscription } from 'rxjs';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-grundstueckflaeche',
  templateUrl: './grundstueckflaeche.component.html',
  providers: [{ provide: ComponentLocationStrategy, useClass: GrundstueckBeschreibungLocationStrategy }],
  imports: [
    BeschreibungTableWrapperComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    HistoryDirective,
    KompaktTimepointComponent,
    RechtsgrundComponent,
    TranslatePipe,
    DecimalHochkommaPipe
  ]
})
export class GrundstueckflaecheComponent extends AbstractComponent implements OnChanges, OnInit, OnDestroy {
  _grundstueckId: GrundstueckId;
  flaechenListLoader: ListLoader<GrundstueckFlaecheMasterDetailEntryMeta>;

  historischeDatenSubscription: Subscription;
  hasPermission: boolean;

  translateKey: string;
  hasParzellenindex: boolean;
  showQualitaetAV: boolean;

  @Input()
  anzeigenAusSichtGeschaeft = false;
  @Input()
  hasRight = true;
  @Input()
  isMeldung = false;

  @Input()
  set flaechenMaessigNichtAusgeschieden(notAusgeschieden: boolean) {
    if (notAusgeschieden) {
      this.translateKey = 'FlaecheNichtAusgeschieden';
    } else {
      this.translateKey = 'Keine';
    }
  }

  constructor(
    private flaecheService: AvflaecheService,
    private timepointService: TimepointService,
    private permissionService: AppPermissionService,
    private appConfigService: AppConfigService,
    private appFormatConfigService: FormatConfigService
  ) {
    super();
    keysToTranslate<string>([
      'FlaecheNichtAusgeschieden',
      'QualitaetAV.AV93',
      'QualitaetAV.PN',
      'QualitaetAV.PN_DEFINITIV',
      'QualitaetAV.PN_PROVISORISCH',
      'QualitaetAV.GRAFISCH',
      'QualitaetAV.UNVERMESSEN',
      'QualitaetAV'
    ]);
  }

  ngOnInit(): void {
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.showQualitaetAV = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_PLUS);
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.historischeDatenSubscription?.unsubscribe();
  }

  @Input()
  set grundstueckId(value: GrundstueckId) {
    this._grundstueckId = value;
    if (!value) {
      return;
    }
    this.hasPermission = this.permissionService.hasPermission('ANZEIGE_GRUNDSTUECK_BESCHREIBUNG');
  }

  get grundstueckId(): GrundstueckId {
    return this._grundstueckId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.grundstueckId?.currentValue) {
      return;
    }
    this.historischeDatenSubscription = this.appConfigService.einstellungHistorischeDaten$.subscribe(() => {
      this.update();
    });
  }

  update(): void {
    const historischeDaten = this.anzeigenAusSichtGeschaeft
      ? HistoryMode.HistoryMode.OHNE_HIST_MIT_GESCHAEFTSAENDERUNGEN
      : this.appConfigService.einstellungHistorischeDaten;
    const timepointCtx = this.timepointService.timepointCtx;
    if (this.hasPermission) {
      if (this.isMeldung) {
        const observable = this.flaecheService
          .loadGrundstueckFlaecheSichtGbMeldung(
            //
            this._grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            tap((entries: Array<GrundstueckFlaecheMasterDetailEntryMeta>) => {
              this.setHasParzellenindex(entries);
            })
          );
        this.flaechenListLoader = new ListLoaderObservable<GrundstueckFlaecheMasterDetailEntryMeta>(undefined, 1, observable);
      } else {
        const observable = this.flaecheService
          .loadGrundstueckFlaecheSichtGrundstueck(
            //
            this._grundstueckId.id, //
            timepointCtx.timepointIso, //
            timepointCtx.timepointId, //
            historischeDaten //
          )
          .pipe(
            tap((entries: Array<GrundstueckFlaecheMasterDetailEntryMeta>) => {
              this.setHasParzellenindex(entries);
            })
          );
        this.flaechenListLoader = new ListLoaderObservable<GrundstueckFlaecheMasterDetailEntryMeta>(undefined, 1, observable);
      }
    }
  }

  private setHasParzellenindex(entries: Array<GrundstueckFlaecheMasterDetailEntryMeta>): void {
    if (entries.length > 0) {
      this.hasParzellenindex = entries[0].versions[0].mParzellenindex.access !== FieldMetaData.AccessEnum.DEACTIVATED;
    }
  }
}
